import Modal from "common/Modal/Modal.tsx";
import Text from "common/Typography/Text";
import Checkbox from "common/Checkbox/Checkbox.tsx";
import { useEffect, useState } from "react";
import Flex from "common/Boxes/Flex.tsx";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage.tsx";
import Button from "common/Buttons/Button.tsx";
import { disabledStyle } from "common/Buttons/styles.ts";
import styled from "styled-components";
import { IS_GOOGLE_PAY } from "features/settings/common/constants.ts";
import { useUser } from "@shared/utils/hooks/misc.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";

interface CancelLastSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  onPayClick: () => void;
  buttonLoading: boolean;
}

const CancelLastSubscriptionModal = ({
  open,
  onClose,
  onPayClick,
  buttonLoading,
}: CancelLastSubscriptionModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { user } = useUser();
  const [checked, setChecked] = useState(false);
  const [showError, setShowError] = useState(false);
  const paymentMethod = IS_GOOGLE_PAY ? "Google Pay" : "Apple Pay";

  useEffect(() => {
    if (checked && showError) {
      setShowError(false);
    }
  }, [checked, showError]);

  const onContinueClick = () => {
    if (!checked) {
      setShowError(true);
    } else {
      onPayClick();
    }
  };

  const footer = (
    <Flex $justifyContent="flex-end" style={{ columnGap: 8 }}>
      <Button onClick={onClose}>
        {t("mobile_billing.cancel_last_subscription_modal.cancel_button")}
      </Button>
      <StyledButton
        type="primary"
        loading={buttonLoading}
        onClick={onContinueClick}
        $disabled={!checked}
      >
        {t("mobile_billing.cancel_last_subscription_modal.submit_button", {
          paymentMethod,
        })}
      </StyledButton>
    </Flex>
  );

  return (
    <Modal
      title={t("mobile_billing.cancel_last_subscription_modal.title")}
      open={open}
      onCancel={onClose}
      footer={footer}
    >
      <Text>
        {t(
          user?.is_trial
            ? "mobile_billing.cancel_last_subscription_modal.free_trial_description"
            : "mobile_billing.cancel_last_subscription_modal.another_payment_method_description",
          { paymentMethod }
        )}
      </Text>
      <Flex
        $alignItems="flex-start"
        style={{ marginTop: 16, columnGap: 8 }}
        onClick={() => setChecked(!checked)}
      >
        <Checkbox checked={checked} />
        <Text>
          {t(
            user?.is_trial
              ? "mobile_billing.cancel_last_subscription_modal.free_trial_checkbox"
              : "mobile_billing.cancel_last_subscription_modal.another_payment_method_checkbox",
            { paymentMethod }
          )}
        </Text>
      </Flex>
      <RequiredFieldMessage
        requirement={showError}
        errorMessage={t(
          "mobile_billing.cancel_last_subscription_modal.checkbox_error"
        )}
      />
    </Modal>
  );
};

export default CancelLastSubscriptionModal;

const StyledButton = styled(Button)<{ $disabled: boolean }>`
  ${(p) => p.$disabled && disabledStyle}
`;
